import React from "react";
import {
  Box,
  Chip,
  LinearProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  useTheme,
} from "@mui/material";
import InventoryIcon from "@mui/icons-material/Inventory";

export const ComplementPopularityStats = ({ startDate, endDate, data }) => {
  const theme = useTheme();

  // Find max values for progress bars
  const maxQuantity = data?.length
    ? Math.max(...data.map((item) => item.totalQuantity))
    : 0;
  const maxAttachRate = data?.length
    ? Math.max(...data.map((item) => item.attachRate))
    : 0;

  // Group complements by category
  const groupedByCategory = data?.reduce((acc, item) => {
    if (!acc[item.category]) {
      acc[item.category] = [];
    }
    acc[item.category].push(item);
    return acc;
  }, {});

  // Calculate total revenue
  const totalRevenue =
    data?.reduce((acc, item) => acc + item.totalRevenue, 0) || 0;

  return (
    <Box>
      <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
        <InventoryIcon
          size={24}
          color={theme.palette.primary.main}
          style={{ marginRight: 8 }}
        />
        <Typography variant="h6" fontWeight="600">
          Complementos Mais Populares
        </Typography>
      </Box>

      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        {startDate ? new Date(startDate).toLocaleDateString() : ""} -{" "}
        {endDate ? new Date(endDate).toLocaleDateString() : ""}
      </Typography>

      <TableContainer>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Complemento</TableCell>
              <TableCell align="right">Quantidade</TableCell>
              <TableCell align="right">Taxa de Adesão</TableCell>
              <TableCell align="right">Receita</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {data?.slice(0, 5).map((item) => (
              <TableRow key={item.id}>
                <TableCell>
                  <Box>
                    <Typography variant="body2" fontWeight={500}>
                      {item.title}
                    </Typography>
                    <Chip
                      label={item.category}
                      size="small"
                      variant="outlined"
                      sx={{ mt: 0.5 }}
                    />
                  </Box>
                </TableCell>
                <TableCell align="right">
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "flex-end",
                    }}
                  >
                    <Typography variant="body2">
                      {item.totalQuantity}
                    </Typography>
                    <Box sx={{ width: 60, mt: 1 }}>
                      <LinearProgress
                        variant="determinate"
                        value={(item.totalQuantity / maxQuantity) * 100}
                        sx={{
                          height: 4,
                          borderRadius: 1,
                          backgroundColor: theme.palette.grey[200],
                          "& .MuiLinearProgress-bar": {
                            backgroundColor: theme.palette.primary.main,
                          },
                        }}
                      />
                    </Box>
                  </Box>
                </TableCell>
                <TableCell align="right">
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "flex-end",
                    }}
                  >
                    <Typography variant="body2">
                      {(item.attachRate * 100).toFixed(1)}%
                    </Typography>
                    <Box sx={{ width: 60, mt: 1 }}>
                      <LinearProgress
                        variant="determinate"
                        value={(item.attachRate / maxAttachRate) * 100}
                        sx={{
                          height: 4,
                          borderRadius: 1,
                          backgroundColor: theme.palette.grey[200],
                          "& .MuiLinearProgress-bar": {
                            backgroundColor: theme.palette.secondary.main,
                          },
                        }}
                      />
                    </Box>
                  </Box>
                </TableCell>
                <TableCell align="right">
                  R$ {item.totalRevenue.toFixed(2)}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <Box sx={{ mt: 3 }}>
        <Typography variant="subtitle2" gutterBottom>
          Categorias de Complementos
        </Typography>

        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, mt: 1 }}>
          {Object.keys(groupedByCategory || {}).map((category) => (
            <Chip
              key={category}
              label={`${category} (${groupedByCategory[category].length})`}
              variant="outlined"
              size="small"
            />
          ))}
        </Box>
      </Box>

      <Box sx={{ mt: 3, display: "flex", justifyContent: "space-between" }}>
        <Typography variant="body2" color="text.secondary">
          Total de Complementos: <strong>{data?.length || 0}</strong>
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Receita Total: <strong>R$ {totalRevenue.toFixed(2)}</strong>
        </Typography>
      </Box>
    </Box>
  );
};
